<template>
  <div class="auth-page theme-page">
    <div class="content-section auth-layout">
      <div class="container">
        <div class="auth-center">
          <div class="row justify-content-center">
            <div class="col-lg-5 col-md-7 col-sm-9 col-12 auth-card theme-panel">
              <div class="auth-brand">
                <div class="brand-mark" aria-hidden="true">
                  <span class="brand-bracket">&#91;</span>
                  <span class="brand-glyph"></span>
                  <span class="brand-bracket">&#93;</span>
                </div>
                <span class="brand-name">Abol Verify</span>
              </div>

              <!-- Done -->
              <div v-if="done" class="state-block text-center">
                <h5 class="title auth-title">{{ isReset ? "Password reset" : "Password set" }}</h5>
                <h6 class="sub-title theme-subtitle auth-copy">
                  Your password has been {{ isReset ? "reset" : "set" }} successfully. You can now log
                  in to your account.
                </h6>
                <router-link :to="{ name: LOGIN_ROUTE }" class="btn btn-dark mt-3">
                  Go to login
                </router-link>
              </div>

              <!-- Set / reset password form -->
              <template v-else>
                <h5 class="text-center title auth-title">
                  {{ isReset ? "Reset your password" : "Set your password" }}
                </h5>
                <h6 class="sub-title theme-subtitle auth-copy text-center">
                  {{
                    isReset
                      ? "Choose a new password for your account."
                      : "Create a password to activate your account."
                  }}
                </h6>
                <p v-if="errorMessage" class="text-center text-danger mt-2">
                  {{ errorMessage }}
                </p>
                <Form class="form" @submit="submit">
                  <div class="form-group">
                    <Field
                      id="password"
                      v-model="form.password"
                      :rules="{ required: ['Password'], min: [6] }"
                      type="password"
                      name="password"
                      class="form-control shadow-none"
                      placeholder="New password"
                    />
                    <ErrorMessage name="password" class="form-text text-danger" />
                  </div>
                  <div class="form-group">
                    <Field
                      id="confirmPassword"
                      v-model="form.confirmPassword"
                      :rules="{ required: ['Confirm Password'] }"
                      type="password"
                      name="confirmPassword"
                      class="form-control shadow-none"
                      placeholder="Confirm password"
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      class="form-text text-danger"
                    />
                  </div>
                  <button
                    type="submit"
                    class="btn btn-dark"
                    :disabled="isLoading ? true : false"
                  >
                    <span
                      v-if="isLoading"
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span v-if="!isLoading">{{ isReset ? "Reset password" : "Set password" }}</span>
                  </button>
                </Form>
              </template>
            </div>
          </div>
        </div>
      </div>
      <span class="text-center copyright auth-footer">
        &copy; Copyright {{ new Date().getFullYear() }} Abol Verify
      </span>
    </div>
  </div>
</template>
<script>
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { LOGIN_ROUTE, RESET_PASSWORD_ROUTE } from "@/router/routes";
import Auth from "@/services/auth";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";

defineRule("required", (value, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

defineRule("min", (value, [limit]) => {
  if (value && value.length < Number(limit)) {
    return `Must be at least ${limit} characters`;
  }
  return true;
});

export default {
  title: "Set Password",
  components: {
    Field,
    Form,
    ErrorMessage,
  },
  data() {
    return {
      LOGIN_ROUTE,
      done: false,
      isLoading: false,
      errorMessage: "",
      form: {
        password: null,
        confirmPassword: null,
      },
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    // The same component backs both the "forgot password" reset link and the
    // staff invite "set password" link — each hits a different endpoint.
    isReset() {
      return this.$route.name === RESET_PASSWORD_ROUTE;
    },
  },
  methods: {
    async submit() {
      this.errorMessage = "";

      if (this.form.password !== this.form.confirmPassword) {
        this.errorMessage = "Passwords do not match.";
        return;
      }

      this.isLoading = true;
      try {
        const body = { newPassword: this.form.password };
        const res = this.isReset
          ? await Auth.resetPassword(this.token, body)
          : await Auth.setPasswordAndVerify(this.token, body);

        if (res && res.data && res.data.status) {
          this.done = true;
          this.$toast.success(
            res.data.msg ||
              (this.isReset
                ? "Password reset successfully."
                : "Password set successfully."),
            TOAST_GLOBAL_CONFIG,
          );
        } else {
          this.errorMessage =
            (res && res.data && res.data.msg) ||
            "Could not update your password. The link may have expired.";
        }
      } catch (e) {
        this.errorMessage = "Could not update your password. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.auth-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 32px;
  height: 32px;
  border-radius: var(--ehs-radius-sm);
  background: linear-gradient(135deg, var(--ehs-color-primary) 0%, #bcdc44 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  letter-spacing: -0.05em;
  box-shadow: 0 4px 12px rgba(115, 196, 29, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.brand-bracket {
  font-family: var(--ehs-font-mono);
  font-size: 1rem;
  font-weight: 600;
  opacity: 0.9;
  line-height: 1;
}

.brand-glyph {
  display: inline-block;
  width: 5px;
  height: 5px;
  background-color: #ffffff;
  border-radius: 1px;
  margin: 0 1px;
}

.brand-name {
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.form {
  margin-top: 1.5rem;
}

.sub-title {
  margin-top: 0.5rem;
}

.state-block {
  padding: 12px 0;
}

@media (max-width: 991.98px) {
  .auth-card {
    padding: 22px 18px !important;
  }

  .auth-center {
    transform: none;
    padding: 24px 0 80px;
  }

  .auth-title {
    font-size: 1.65rem !important;
  }
}
</style>
